import { useQuery } from "@tanstack/react-query";
import { NavLink, Outlet, useLocation } from "react-router";
import { admin, claims } from "../lib/api";
import VerifyEmailBanner from "./VerifyEmailBanner";
import AgreementsBanner from "./AgreementsBanner";
import { useAuth } from "../lib/auth";
import type { Domain, List, SupportSummary } from "../lib/types";

// Iconițe SVG (stroke) — fără dependență de fonturi emoji.
const I = {
  home: "M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z",
  orders: "M6 3h12v18l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6",
  products: "M3 7l9-4 9 4-9 4zM3 7v10l9 4 9-4V7M12 11v10",
  categories: "M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z",
  customers: "M16 19v-1a4 4 0 0 0-8 0v1M12 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM20 19v-1a3 3 0 0 0-3-3M17 5a3 3 0 0 1 0 6",
  discounts: "M3 12V4h8l10 10-8 8zM7.5 7.5h.01",
  shipping: "M3 6h11v10H3zM14 10h4l3 3v3h-7M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z",
  pages: "M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h6",
  aspect: "M12 3a9 9 0 1 0 0 18c1 0 1.5-.8 1.5-1.5 0-.4-.2-.8-.4-1.1-.3-.3-.4-.7-.4-1.1 0-.8.7-1.5 1.5-1.5H16a5 5 0 0 0 5-5c0-4.4-4-7.8-9-7.8zM7.5 12a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM10.5 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM15 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2z",
  seo: "M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM21 21l-5-5M8 11h6M11 8v6",
  support: "M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.4A8 8 0 1 1 21 12zM9 10h.01M12 10h.01M15 10h.01",
  settings: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19 12l2-1-1-3-2 .5-1.5-1.5.5-2-3-1-1 2h-2l-1-2-3 1 .5 2L6 7.5 4 7 3 10l2 1v2l-2 1 1 3 2-.5 1.5 1.5-.5 2 3 1 1-2h2l1 2 3-1-.5-2 1.5-1.5 2 .5 1-3-2-1z",
};

function Icon({ d }: { d: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={d} />
    </svg>
  );
}

const NAV = [
  { to: "/", label: "Panou", icon: I.home, end: true },
  { to: "/orders", label: "Comenzi", icon: I.orders },
  { to: "/products", label: "Produse", icon: I.products },
  { to: "/categories", label: "Categorii", icon: I.categories },
  { to: "/customers", label: "Clienți", icon: I.customers },
  { to: "/discounts", label: "Reduceri", icon: I.discounts },
  { to: "/shipping", label: "Livrare", icon: I.shipping },
  { to: "/aspect", label: "Aspect", icon: I.aspect },
  { to: "/pages", label: "Pagini", icon: I.pages },
  { to: "/seo", label: "SEO", icon: I.seo },
  { to: "/support", label: "Suport", icon: I.support },
  { to: "/settings", label: "Setări", icon: I.settings },
];

export default function Layout() {
  // Editorul de aspect folosește toată lățimea (bara de setări + previzualizarea magazinului).
  const full = useLocation().pathname === "/aspect";
  const { me, logout } = useAuth();
  const domains = useQuery({ queryKey: ["domains"], queryFn: () => admin.get<List<Domain>>("/domains") });
  // Badge pe „Suport” când echipa platformei a răspuns și se așteaptă reacția comerciantului.
  const support = useQuery({ queryKey: ["support-summary"], queryFn: () => admin.get<SupportSummary>("/support/summary"), refetchInterval: 60_000 });
  const badge: Record<string, number | undefined> = { "/support": support.data?.waiting_merchant };
  const domain = domains.data?.data.find((d) => d.is_primary)?.host ?? domains.data?.data[0]?.host ?? "";
  return (
    <div className="flex min-h-screen">
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-slate-200 bg-white md:flex">
        <div className="border-b border-slate-100 px-5 py-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-brand">shopcore</div>
          <div className="truncate font-bold">{me?.store.name ?? "…"}</div>
          {domain && <div className="truncate text-xs text-slate-400">{domain}</div>}
        </div>
        <nav className="flex-1 space-y-0.5 p-3">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${isActive ? "bg-brand-soft text-brand" : "text-slate-600 hover:bg-slate-50"}`}>
              <Icon d={n.icon} />{n.label}
              {badge[n.to] ? <span className="ml-auto rounded-full bg-brand px-1.5 py-0.5 text-xs font-bold text-white">{badge[n.to]}</span> : null}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-100 p-4 text-sm">
          <div className="truncate font-medium">{me?.user?.name || me?.user?.email}</div>
          <div className="mb-2 truncate text-xs text-slate-400">{me?.user?.role}</div>
          <button onClick={logout} className="text-xs font-semibold text-slate-500 hover:text-red-600">Deconectare</button>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <nav className="flex gap-1 overflow-x-auto border-b border-slate-200 bg-white px-3 py-2 md:hidden">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `whitespace-nowrap rounded-md px-3 py-1.5 text-sm ${isActive ? "bg-brand-soft text-brand" : "text-slate-600"}`}>
              {n.label}
              {badge[n.to] ? <span className="ml-1 rounded-full bg-brand px-1.5 text-xs font-bold text-white">{badge[n.to]}</span> : null}
            </NavLink>
          ))}
        </nav>
        {me?.user && me.user.email_verified === false && !claims()?.imp && <VerifyEmailBanner email={me.user.email} />}
        <AgreementsBanner />
        {claims()?.imp && (
          <div className="flex items-center justify-between gap-4 bg-amber-400 px-4 py-2 text-sm font-semibold text-amber-950">
            <span>Sesiune de suport a operatorului platformei · expiră la {new Date((claims()?.exp ?? 0) * 1000).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })} · acțiunile sunt înregistrate</span>
            <button onClick={logout} className="rounded bg-amber-950/10 px-2 py-1 hover:bg-amber-950/20">Închide sesiunea</button>
          </div>
        )}
        {me?.store.status === "suspended" && (
          <div className="bg-red-600 px-4 py-2 text-sm font-semibold text-white">
            Magazinul este suspendat și nu este vizibil clienților{me.store.suspended_reason ? `: ${me.store.suspended_reason}` : ""}. Contactează suportul platformei.
          </div>
        )}
        <main className={full ? "flex min-h-0 w-full flex-1 flex-col" : "mx-auto w-full max-w-6xl flex-1 p-4 sm:p-8"}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
